// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

type HeaderState = Readonly<{
    isSticky: boolean;
    mobileSearch: boolean;
    globalMessage: boolean;
    searchInputFocus: boolean;
    headerSignInDrawerOpen: boolean;
    isFeaturedBrandsLoading: boolean;
    featuredBrands?: BrandModel[];
    creditCheckModalDidOpen: boolean;
    creditCheckCallIsSubmitted: boolean;
    headerLinks: [];
}>;

const initialHeaderState: HeaderState = {
    isSticky: false,
    mobileSearch: false,
    globalMessage: true,
    searchInputFocus: false,
    headerSignInDrawerOpen: false,
    isFeaturedBrandsLoading: false,
    featuredBrands: undefined,
    creditCheckModalDidOpen: false,
    creditCheckCallIsSubmitted: false,
    headerLinks: [],
};

const reducer = {
    "xHeader/isSticky": (draft: Draft<HeaderState>, action: { isSticky: boolean }) => {
        draft.isSticky = action.isSticky;
    },
    "xHeader/mobileSearch": (draft: Draft<HeaderState>, action: { mobileSearch: boolean }) => {
        draft.mobileSearch = action.mobileSearch;
    },
    "xHeader/globalMessage": (draft: Draft<HeaderState>, action: { globalMessage: boolean }) => {
        draft.globalMessage = action.globalMessage;
    },

    "xHeader/searchInputFocus": (draft: Draft<HeaderState>, action: { focus: boolean }) => {
        draft.searchInputFocus = action.focus;
    },
    "xHeader/headerSignInDrawerOpen": (draft: Draft<HeaderState>, action: { open: boolean }) => {
        draft.headerSignInDrawerOpen = action.open;
    },
    "xHeader/featuredBrands": (
        draft: Draft<HeaderState>,
        action: { featuredBrands: []; isFeaturedBrandsLoading?: boolean },
    ) => {
        if (action.isFeaturedBrandsLoading) {
            draft.isFeaturedBrandsLoading = true;
            return;
        }
        draft.featuredBrands = action.featuredBrands;
        draft.isFeaturedBrandsLoading = false;
    },
    "xHeader/creditCheckModalDidOpen": (draft: Draft<HeaderState>, action: { creditCheckModalDidOpen: boolean }) => {
        draft.creditCheckModalDidOpen = action.creditCheckModalDidOpen;
    },
    "xHeader/creditCheckCallIsSubmitted": (
        draft: Draft<HeaderState>,
        action: { creditCheckCallIsSubmitted: boolean },
    ) => {
        draft.creditCheckCallIsSubmitted = action.creditCheckCallIsSubmitted;
    },
    "xHeader/setHeaderLinks": (draft: Draft<HeaderState>, action: { headerLinks: [] }) => {
        draft.headerLinks = action.headerLinks;
    },
};

const xHeader = createTypedReducerWithImmer(initialHeaderState, reducer);

(reducers as any).xHeader = xHeader;

export type xHeaderState = {
    xHeader: HeaderState;
};

type xHeaderReducers = {
    isSticky: typeof xHeader;
    mobileSearch: typeof xHeader;
    globalMessage: typeof xHeader;
    searchInputFocus: typeof xHeader;
    headerSignInDrawerOpen: typeof xHeader;
    featuredBrands: typeof xHeader;
    creditCheckModalDidOpen: typeof xHeader;
    creditCheckCallIsSubmitted: typeof xHeader;
    setHeaderLinks: typeof xHeader;
};

export type xHeaderActions = Parameters<xHeaderReducers[keyof xHeaderReducers]>[1] | AnyAction;
